import { Check, PlusCircle, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { amountToWordsINR } from '../../utils/amountInWords';
import { getTenantInfo } from '../OrgManagement/employeeApiService';
import { customerApi } from '../Onboarding/CustomerOnboarding/customerApi';
import { getAllSites, getSiteDetails } from '../SiteManagement/siteApi';
import {
  buildCustomerFromRecord,
  buildVendorFromSite,
  fetchBankDetailsForQuotation,
  toBankDetailsApiPayload,
} from './quotationPartyHelpers';
import PrimaryButton from '../ui/PrimaryButton';
import RichTextEditor from '../ui/RichTextEditor';
import Input from '../ui/input';
import { quotationApi } from './quotationApi';
import {
  buildPocClientFromForm,
  buildPocVendorFromForm,
} from './quotationPocHelpers';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const createEmptyLineItem = () => ({
  name: '',
  hsnSacCode: '',
  quantity: '',
  units: '',
  rate: '',
  amount: '',
});

const numberOrZero = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveSiteValue = (site) => {
  const value =
    site?.site_id ??
    site?.siteId ??
    site?.id ??
    site?._id ??
    site?.site_code ??
    site?.siteCode;
  return value !== undefined && value !== null ? String(value) : '';
};

const resolveSiteLabel = (site) =>
  site?.site_name ||
  site?.siteName ||
  site?.siteCode ||
  site?.site_code ||
  `Site ${site?.site_id ?? site?.id ?? ''}`;

const EstimationQuotation = () => {
  // Log screen view
  useScreenLogging('EstimationQuotation', {
    component: 'EstimationQuotation',
  });

  const { openOverlay } = useOverlay();
  const [formData, setFormData] = useState({
    siteSelection: '',
    clientSelectionShipping: '',
    date: getTodayDate(),
    paymentMode: '',
    paymentTerms: '',
    expiryDate: '',
    shippingPackages: '',
    grossQuantity: '',
    netQuantity: '',
    pocVendorName: '',
    pocVendorPhone: '',
    pocVendorRole: '',
    pocVendorEmail: '',
    pocVendorGstin: '',
    pocClientName: '',
    pocClientPhone: '',
    pocClientRole: '',
    pocClientEmail: '',
    pocClientGstin: '',
    specialNotes: '',
    termsOfSale: '',
    vendorSign: '',
    clientSign: '',
  });

  const [lineItems, setLineItems] = useState([createEmptyLineItem()]);
  const [charges, setCharges] = useState({
    shippingHandling: '',
    insurance: '',
  });
  const [hsnSacRateByCode, setHsnSacRateByCode] = useState({});
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [isCustomersLoading, setIsCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ success: '', error: '' });
  const [isMobileView, setIsMobileView] = useState(false);
  const [isVendorSigned, setIsVendorSigned] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);

  const formatCurrency = (amount = 0) =>
    Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(Number.isFinite(amount) ? amount : 0);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFeedback({ success: '', error: '' });
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      if (field === 'quantity' || field === 'rate') {
        const rawQuantity =
          field === 'quantity' ? numberOrZero(value) : numberOrZero(updated.quantity);
        const rawRate =
          field === 'rate' ? numberOrZero(value) : numberOrZero(updated.rate);
        const quantity = Math.max(0, rawQuantity);
        const rate = Math.max(0, rawRate);
        const amount = quantity * rate;
        updated.amount = amount > 0 ? amount.toFixed(2) : '';
      }
      next[index] = updated;
      return next;
    });
  };

  const handleAddLineItem = () => {
    setLineItems((prev) => [...prev, createEmptyLineItem()]);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleHsnSacRateChange = (code, field, value) => {
    setHsnSacRateByCode((prev) => ({
      ...prev,
      [code]: {
        centralRate:
          field === 'centralRate' ? value : (prev[code]?.centralRate ?? ''),
        stateUtRate: field === 'stateUtRate' ? value : (prev[code]?.stateUtRate ?? ''),
        igstRate: field === 'igstRate' ? value : (prev[code]?.igstRate ?? ''),
      },
    }));
  };

  const handleChargesChange = (field, value) => {
    setCharges((prev) => ({ ...prev, [field]: value }));
  };

  const summary = useMemo(() => {
    const subtotal = lineItems.reduce(
      (sum, item) => sum + (parseFloat(item.amount) || 0),
      0
    );
    const shippingHandling = numberOrZero(charges.shippingHandling);
    const insurance = numberOrZero(charges.insurance);
    const netTotalBeforeTax = subtotal + shippingHandling + insurance;
    const totalQuantity = lineItems.reduce(
      (sum, item) => sum + numberOrZero(item.quantity),
      0
    );

    return {
      subtotal,
      subtotalAfterDiscount: subtotal,
      shippingHandling,
      insurance,
      netTotalBeforeTax,
      totalQuantity,
    };
  }, [charges, lineItems]);

  const formatTotalQuantity = (qty) =>
    Number.isInteger(qty) ? String(qty) : qty.toFixed(2);

  const formatTaxTableAmount = (amount = 0) =>
    Number(amount || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const hsnSacTaxSummary = useMemo(() => {
    const groups = new Map();
    lineItems.forEach((item) => {
      const code = String(item.hsnSacCode || '').trim() || '—';
      const amount = parseFloat(item.amount) || 0;
      const existing = groups.get(code) || { code, taxableValue: 0 };
      existing.taxableValue += amount;
      groups.set(code, existing);
    });

    const rawSubtotal = lineItems.reduce(
      (sum, item) => sum + (parseFloat(item.amount) || 0),
      0
    );
    const discountFactor =
      rawSubtotal > 0 ? summary.subtotalAfterDiscount / rawSubtotal : 1;

    let rows = Array.from(groups.values()).map((group) => {
      const taxableValue = group.taxableValue * discountFactor;
      const stored = hsnSacRateByCode[group.code] || {};
      const centralRate =
        stored.centralRate !== undefined && stored.centralRate !== ''
          ? numberOrZero(stored.centralRate)
          : 0;
      const stateRate =
        stored.stateUtRate !== undefined && stored.stateUtRate !== ''
          ? numberOrZero(stored.stateUtRate)
          : 0;
      const igstRate =
        stored.igstRate !== undefined && stored.igstRate !== ''
          ? numberOrZero(stored.igstRate)
          : 0;
      const rowCombinedRate = centralRate + stateRate + igstRate;
      let centralAmount = 0;
      let stateAmount = 0;
      let igstAmount = 0;

      if (rowCombinedRate > 0) {
        centralAmount = taxableValue * (centralRate / 100);
        stateAmount = taxableValue * (stateRate / 100);
        igstAmount = taxableValue * (igstRate / 100);
      }

      return {
        code: group.code,
        taxableValue,
        centralRate,
        centralRateInput: stored.centralRate ?? '',
        stateRate,
        stateUtRateInput: stored.stateUtRate ?? '',
        igstRate,
        igstRateInput: stored.igstRate ?? '',
        centralAmount,
        stateAmount,
        igstAmount,
        totalTax: centralAmount + stateAmount + igstAmount,
      };
    });

    rows.sort((a, b) => a.code.localeCompare(b.code));
    rows = rows.filter((row) => row.taxableValue > 0);

    const totals = rows.reduce(
      (acc, row) => ({
        taxableValue: acc.taxableValue + row.taxableValue,
        centralAmount: acc.centralAmount + row.centralAmount,
        stateAmount: acc.stateAmount + row.stateAmount,
        igstAmount: acc.igstAmount + row.igstAmount,
        totalTax: acc.totalTax + row.totalTax,
      }),
      {
        taxableValue: 0,
        centralAmount: 0,
        stateAmount: 0,
        igstAmount: 0,
        totalTax: 0,
      }
    );

    return { rows, totals };
  }, [lineItems, summary.subtotalAfterDiscount, hsnSacRateByCode]);

  const lineItemsGstTotal = hsnSacTaxSummary.totals.totalTax;
  const lineItemsGrandTotal = summary.subtotal + lineItemsGstTotal;
  const documentGrandTotal = summary.netTotalBeforeTax + lineItemsGstTotal;

  const amountInWords = useMemo(
    () => (documentGrandTotal > 0 ? amountToWordsINR(documentGrandTotal) : ''),
    [documentGrandTotal]
  );

  const gstFooterLabel = useMemo(() => {
    if (summary.subtotal > 0 && lineItemsGstTotal > 0) {
      const pct = ((lineItemsGstTotal / summary.subtotal) * 100).toFixed(2);
      return `GST(${pct}%)`;
    }
    return 'GST';
  }, [summary.subtotal, lineItemsGstTotal]);

  useEffect(() => {
    let isMounted = true;
    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError('');
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
            ? response
            : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        if (isMounted) {
          setSites(sorted);
        }
      } catch (error) {
        if (isMounted) {
          setSitesError(
            error?.message || 'Unable to load sites. Please try again later.'
          );
        }
      } finally {
        if (isMounted) {
          setIsSitesLoading(false);
        }
      }
    };

    fetchSites();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchTenant = async () => {
      try {
        const tenantData = await getTenantInfo();
        if (isMounted) {
          setTenantInfo(tenantData);
        }
      } catch (error) {
        console.warn('[EstimationQuotation] Unable to load tenant info for PDF:', error);
      }
    };

    fetchTenant();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchCustomers = async () => {
      setIsCustomersLoading(true);
      setCustomersError('');
      try {
        const result = await customerApi.listCustomers({ limit: 100, status: 'active' });
        if (result.success) {
          const sorted = [...(result.customers || [])].sort((a, b) =>
            (a?.customer_name || a?.name || '').localeCompare(b?.customer_name || b?.name || '')
          );
          if (isMounted) {
            setCustomers(sorted);
            // Clear selection if the selected customer no longer exists
            if (formData.clientSelectionShipping) {
              const stillExists = sorted.some(c => (c.customer_id || c.id) === formData.clientSelectionShipping);
              if (!stillExists) {
                console.warn('Selected customer no longer exists, clearing selection');
                handleFormChange('clientSelectionShipping', '');
              }
            }
          }
        } else {
          if (isMounted) {
            setCustomersError(result.error || 'Failed to load customers');
          }
        }
      } catch (error) {
        if (isMounted) {
          setCustomersError(error?.message || 'Unable to load customers. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsCustomersLoading(false);
        }
      }
    };

    fetchCustomers();
    return () => {
      isMounted = false;
    };
  }, []);

  const siteOptions = useMemo(
    () =>
      sites
        .map((site) => ({
          value: resolveSiteValue(site),
          label: resolveSiteLabel(site),
        }))
        .filter((option) => option.value),
    [sites]
  );

  const findSiteLabel = (value) =>
    siteOptions.find((option) => option.value === value)?.label || '';

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const updateView = (event) => setIsMobileView(event.matches);
    updateView(mediaQuery);
    mediaQuery.addEventListener('change', updateView);

    return () => mediaQuery.removeEventListener('change', updateView);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!formData.siteSelection) {
      setFeedback({ success: '', error: 'Please select a site' });
      return;
    }

    if (!formData.clientSelectionShipping) {
      setFeedback({ success: '', error: 'Please select a client/customer' });
      return;
    }

    if (lineItems.length === 0 || lineItems.some(item => !item.name || !item.quantity || !item.rate)) {
      setFeedback({ success: '', error: 'Please add at least one valid line item with name, quantity, and rate' });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ success: '', error: '' });

    try {
      // Find customer by ID
      const selectedCustomer = customers.find(
        c => {
          const cId = c.customer_id || c.id;
          return cId === formData.clientSelectionShipping;
        }
      );

      // Find site details
      const selectedSite = sites.find(s => resolveSiteValue(s) === formData.siteSelection);
      const siteName = selectedSite?.site_name || selectedSite?.siteName || '';

      const mappedItems = lineItems.map((item) => {
        const quantity = parseFloat(item.quantity) || 0;
        const rate = parseFloat(item.rate) || 0;
        const code = String(item.hsnSacCode || '').trim() || '—';
        const hsnRow = hsnSacTaxSummary.rows.find((row) => row.code === code);
        const combinedRate = hsnRow
          ? hsnRow.centralRate + hsnRow.stateRate + hsnRow.igstRate
          : 0;

        return {
          name: item.name || '',
          description: item.name || '',
          quantity_units: item.units || 'unit',
          quantity_value: quantity,
          rate: rate,
          ...(item.hsnSacCode?.trim() && {
            hsn_code: item.hsnSacCode.trim(),
            sac_code: item.hsnSacCode.trim(),
          }),
          tax_rate: combinedRate > 0 ? parseFloat(combinedRate.toFixed(2)) : undefined,
        };
      });

      const pocVendor = buildPocVendorFromForm(formData);
      const pocClient = buildPocClientFromForm(formData);

      let detailedSite = selectedSite;
      if (formData.siteSelection) {
        try {
          detailedSite = await getSiteDetails(formData.siteSelection);
        } catch (siteError) {
          console.warn('[EstimationQuotation] Using cached site data for bank details:', siteError);
        }
      }

      let resolvedTenantInfo = tenantInfo;
      try {
        resolvedTenantInfo = await getTenantInfo();
      } catch (tenantError) {
        console.warn('[EstimationQuotation] Using cached tenant info for bank details:', tenantError);
      }

      const bankDetails = await fetchBankDetailsForQuotation({
        siteId: formData.siteSelection,
        site: detailedSite,
        tenantInfo: resolvedTenantInfo,
      });
      const bankDetailsApi = toBankDetailsApiPayload(bankDetails);

      // Format dates
      const issuedDate = formData.date 
        ? new Date(formData.date).toISOString() 
        : new Date().toISOString();
      const expiryDate = formData.expiryDate 
        ? new Date(formData.expiryDate).toISOString() 
        : undefined;

      // Build API payload
      const apiPayload = {
        site_id: formData.siteSelection,
        site_name: siteName,
        customer_id: selectedCustomer ? (selectedCustomer.customer_id || selectedCustomer.id) : undefined,
        client_name: selectedCustomer 
          ? (selectedCustomer.customer_name || selectedCustomer.name) 
          : formData.clientSelectionShipping, // Fallback to text input if no customer selected
        items: mappedItems,
        subtotal: summary.subtotal,
        tax_total: lineItemsGstTotal,
        tax_base_amount: summary.subtotal,
        shipping_handling: parseFloat(charges.shippingHandling) || 0,
        insurance: parseFloat(charges.insurance) || 0,
        grand_total: documentGrandTotal,
        payment_mode: formData.paymentMode || undefined,
        payment_terms: formData.paymentTerms || undefined,
        issued_date: issuedDate,
        expiry_date: expiryDate,
        estimate_gross_quantity: formData.grossQuantity || undefined,
        estimate_net_quantity: formData.netQuantity || undefined,
        special_notes: formData.specialNotes || undefined,
        terms_of_sale: formData.termsOfSale || undefined,
        poc_vendor: pocVendor,
        poc_client: pocClient,
        ...(bankDetailsApi && { bank_details: bankDetailsApi }),
        status: 'draft',
        document_upload_mode: 'presign', // Use presign mode for document upload
      };

      console.log('Creating quotation with payload:', apiPayload);

      // Create quotation via API
      const result = await quotationApi.createQuotation(apiPayload);

      if (result.success) {
        const quotation = result.quotation;
        setFeedback({ 
          success: `Quotation created successfully! Quotation Number: ${quotation?.quotation_number || 'N/A'}`,
          error: '' 
        });
        
        let detailedCustomer = selectedCustomer;
        const customerId = selectedCustomer?.customer_id || selectedCustomer?.id;
        if (customerId) {
          try {
            const customerResult = await customerApi.getCustomer(customerId);
            if (customerResult.success && customerResult.customer) {
              detailedCustomer = customerResult.customer;
            }
          } catch (customerError) {
            console.warn('[EstimationQuotation] Using cached customer data for PDF:', customerError);
          }
        }

        const siteLabel =
          detailedSite?.site_name ||
          detailedSite?.siteName ||
          selectedSite?.site_name ||
          selectedSite?.siteName ||
          findSiteLabel(formData.siteSelection);
        const vendor = buildVendorFromSite(detailedSite || selectedSite, {
          tenantInfo: resolvedTenantInfo,
          formData,
          siteLabel,
        });
        const printBankDetails = await fetchBankDetailsForQuotation({
          siteId: formData.siteSelection,
          site: detailedSite || selectedSite,
          tenantInfo: resolvedTenantInfo,
          apiQuotation: quotation,
        });
        const customer = buildCustomerFromRecord(detailedCustomer, {
          formData,
          fallbackName: selectedCustomer
            ? selectedCustomer.customer_name || selectedCustomer.name
            : formData.clientSelectionShipping,
        });

        // Also open the print overlay with the created quotation data
        const quotationPayload = {
          quotationNumber: quotation?.quotation_number || `QTN-${Date.now().toString().slice(-6)}`,
          generatedAt: new Date().toISOString(),
          siteId: formData.siteSelection,
          siteName: siteLabel,
          selectedSite: detailedSite || selectedSite || null,
          selectedCustomer: detailedCustomer || selectedCustomer || null,
          vendor,
          customer,
          brand: {
            companyName: siteLabel,
            gstin: vendor?.gstin,
          },
          formData: { ...formData },
          lineItems: lineItems.map((item, index) => ({
            ...item,
            lineNumber: index + 1,
            sacCode: item.hsnSacCode || '',
            amount: item.amount || (parseFloat(item.quantity) * parseFloat(item.rate)).toFixed(2),
          })),
          hsnSacTaxSummary: {
            rows: hsnSacTaxSummary.rows.map((row) => ({ ...row })),
            totals: { ...hsnSacTaxSummary.totals },
          },
          charges: { ...charges },
          amountInWords,
          summary: {
            ...summary,
            totalTax: lineItemsGstTotal,
            grandTotal: documentGrandTotal,
            amountInWords,
          },
          labels: {
            site: siteLabel,
            client: selectedCustomer 
              ? (selectedCustomer.customer_name || selectedCustomer.name)
              : formData.clientSelectionShipping,
          },
          bankDetails: printBankDetails,
          tenantInfo: resolvedTenantInfo,
          apiQuotation: quotation,
        };
        
        openOverlay('estimationQuotationPrint', { quotation: quotationPayload });
      } else {
        // Check if it's a customer not found error
        if (result.error && result.error.includes('Customer not found')) {
          throw new Error(
            `Customer not found (404). The selected customer does not exist in the quotation system. ` +
            `Please try selecting a different customer or contact support if the issue persists.`
          );
        }
        throw new Error(result.error || 'Failed to create quotation');
      }
    } catch (error) {
      console.error('Error creating quotation:', error);
      setFeedback({ 
        success: '', 
        error: error.message || 'Failed to create quotation. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPocSection = (prefix, title, { includeGstin = false } = {}) => (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <div>
        <label className="text-xs font-semibold text-gray-500">Name</label>
        <Input
          value={formData[`${prefix}Name`]}
          onChange={(e) => handleFormChange(`${prefix}Name`, e.target.value)}
          placeholder="Enter name"
          className="mt-1 h-12 rounded-2xl border border-emerald-200 bg-white px-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
        />
      </div>
      <div>
        <label className="text-xs font-semibold text-gray-500">Role</label>
        <Input
          value={formData[`${prefix}Role`]}
          onChange={(e) => handleFormChange(`${prefix}Role`, e.target.value)}
          placeholder="Enter role"
          className="mt-1 h-12 rounded-2xl border border-emerald-200 bg-white px-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
        />
      </div>
      <div>
        <label className="text-xs font-semibold text-gray-500">Mobile No.</label>
        <Input
          value={formData[`${prefix}Phone`]}
          onChange={(e) => handleFormChange(`${prefix}Phone`, e.target.value)}
          placeholder="+91 98765 43210"
          className="mt-1 h-12 rounded-2xl border border-emerald-200 bg-white px-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
        />
      </div>
      <div>
        <label className="text-xs font-semibold text-gray-500">Email</label>
        <Input
          value={formData[`${prefix}Email`]}
          onChange={(e) => handleFormChange(`${prefix}Email`, e.target.value)}
          placeholder="name@company.com"
          className="mt-1 h-12 rounded-2xl border border-emerald-200 bg-white px-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
        />
      </div>
      {includeGstin && (
        <div>
          <label className="text-xs font-semibold text-gray-500">GSTIN</label>
          <Input
            value={formData[`${prefix}Gstin`]}
            onChange={(e) =>
              handleFormChange(`${prefix}Gstin`, e.target.value.toUpperCase())
            }
            placeholder="e.g. 29ABCDE1234F1Z5"
            maxLength={15}
            className="mt-1 h-12 rounded-2xl border border-emerald-200 bg-white px-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
          />
        </div>
      )}
    </div>
  );

  const handleVendorSignToggle = () => {
    setIsVendorSigned((prev) => {
      const next = !prev;
      handleFormChange('vendorSign', next ? 'signed' : '');
      return next;
    });
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto max-w-7xl">
        <header className="mb-8 text-center">
          <h1 className="text-3xl font-semibold text-gray-900">Estimation/ Quotation</h1>
          <p className="mt-2 text-gray-500">
            Create and review estimation quotation details before submission.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Row 1: Site Selection + Client Selection Shipping */}
          <section className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-sm font-semibold text-gray-900">
                Site Selection (From where quotation is being raised)
              </p>
              <div className="mt-4">
                <select
                  value={formData.siteSelection}
                  onChange={(e) => handleFormChange('siteSelection', e.target.value)}
                  className="h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  disabled={isSitesLoading}
                >
                  <option value="" disabled hidden>
                    Select site
                  </option>
                  {siteOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {sitesError && (
                  <p className="mt-1 text-xs text-red-600">{sitesError}</p>
                )}
                {!isSitesLoading && !siteOptions.length && !sitesError && (
                  <p className="mt-1 text-xs text-gray-500">
                    No sites available. Please create a site first.
                  </p>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-sm font-semibold text-gray-900">
                Client Selection Shipping
              </p>
              <div className="mt-4">
                <select
                  value={formData.clientSelectionShipping}
                  onChange={(e) => handleFormChange('clientSelectionShipping', e.target.value)}
                  className="h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  disabled={isCustomersLoading}
                >
                  <option value="" disabled hidden>
                    Select customer
                  </option>
                  {customers.map((customer) => {
                    const customerId = customer.customer_id || customer.id;
                    const customerName = customer.customer_name || customer.name || 'Unknown';
                    return (
                      <option key={customerId} value={customerId}>
                        {customerName}
                      </option>
                    );
                  })}
                </select>
                {customersError && (
                  <p className="mt-1 text-xs text-red-600">{customersError}</p>
                )}
                {!isCustomersLoading && !customers.length && !customersError && (
                  <p className="mt-1 text-xs text-gray-500">
                    No customers available. Please create a customer first.
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Row 2: Date, Payment Mode, Payment Terms, Expiry Date + Shipping Details */}
          <section className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-sm font-semibold text-gray-900">Quotation Information</p>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-500">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleFormChange('date', e.target.value)}
                    className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Payment Mode
                  </label>
                  <select
                    value={formData.paymentMode}
                    onChange={(e) => handleFormChange('paymentMode', e.target.value)}
                    className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  >
                    <option value="" disabled>
                      Select mode
                    </option>
                    <option value="bank-transfer">Bank Transfer</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-gray-500">
                    Payment Terms
                  </label>
                  <textarea
                    value={formData.paymentTerms}
                    onChange={(e) => handleFormChange('paymentTerms', e.target.value)}
                    placeholder="e.g., Net 30, milestone-wise terms, etc."
                    rows={2}
                    className="mt-1 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => handleFormChange('expiryDate', e.target.value)}
                    className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-sm font-semibold text-gray-900">Shipping Details</p>
              <div className="mt-4 space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    No. of Items / Packages
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.shippingPackages}
                    onChange={(e) =>
                      handleFormChange('shippingPackages', e.target.value)
                    }
                    placeholder="0"
                    className="mt-1 h-12 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Est. Gross Quantity
                  </label>
                  <Input
                    value={formData.grossQuantity}
                    onChange={(e) => handleFormChange('grossQuantity', e.target.value)}
                    placeholder="In Kg / Units"
                    className="mt-1 h-12 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Est. Net Quantity
                  </label>
                  <Input
                    value={formData.netQuantity}
                    onChange={(e) => handleFormChange('netQuantity', e.target.value)}
                    placeholder="In Kg / Units"
                    className="mt-1 h-12 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Row 3a: POC Information */}
          <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">POC Information</p>
            <div className="mt-4 grid gap-6 lg:grid-cols-2">
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                {renderPocSection('pocVendor', 'POC from Vendor', { includeGstin: true })}
              </div>
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                {renderPocSection('pocClient', 'POC from Client', { includeGstin: true })}
              </div>
            </div>
          </section>

          {/* Row 3b: Special Notes & Terms */}
          <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">
              Special Notes & Terms of Sale
            </p>
            <div className="mt-4 grid gap-6 lg:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-gray-500">Special Notes</label>
                <textarea
                  value={formData.specialNotes}
                  onChange={(e) => handleFormChange('specialNotes', e.target.value)}
                  placeholder="Special delivery notes, handling instructions..."
                  className="h-28 w-full resize-none rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs font-semibold text-gray-500">Terms of Sale</label>
                <RichTextEditor
                  value={formData.termsOfSale}
                  onChange={(value) => handleFormChange('termsOfSale', value)}
                  placeholder="Terms of sale"
                  minHeight={112}
                  className="bg-white"
                />
              </div>
            </div>
          </section>

          {/* Row 4: Line Items Table */}
          <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">Line Items</p>
                <p className="text-sm text-gray-500">
                  Manage services, quantities, units, and rates. Amount is auto-calculated.
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddLineItem}
                className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-500 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
              >
                <PlusCircle className="h-4 w-4" />
                Add Line Item
              </button>
            </div>

            <div className="mt-6">
              {!isMobileView ? (
                <div className="overflow-x-auto rounded-2xl border border-gray-100">
                  <table className="min-w-full border-collapse text-sm">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold">S. No</th>
                        <th className="px-4 py-3 text-left font-semibold min-w-[200px]">
                          Name <span className="text-red-500">*</span>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold min-w-[140px]">
                          SAC / HSN Code
                        </th>
                        <th className="px-4 py-3 text-left font-semibold min-w-[120px]">
                          Quantity <span className="text-red-500">*</span>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold min-w-[140px]">
                          Units (Per item)
                        </th>
                        <th className="px-4 py-3 text-left font-semibold min-w-[140px]">
                          Rate <span className="text-red-500">*</span>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold min-w-[140px]">
                          Total Amount
                        </th>
                        <th className="px-4 py-3 text-right font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item, index) => (
                        <tr key={`line-item-${index}`} className="border-t border-gray-100">
                          <td className="px-4 py-4 text-gray-700">{index + 1}</td>
                          <td className="px-4 py-4">
                            <Input
                              value={item.name}
                              onChange={(e) =>
                                handleLineItemChange(index, 'name', e.target.value)
                              }
                              placeholder="Item or service name"
                              className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                            />
                          </td>
                          <td className="px-4 py-4">
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              value={item.hsnSacCode}
                              onChange={(e) =>
                                handleLineItemChange(index, 'hsnSacCode', e.target.value)
                              }
                              placeholder="Code"
                              className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                            />
                          </td>
                          <td className="px-4 py-4">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) =>
                                handleLineItemChange(index, 'quantity', e.target.value)
                              }
                              placeholder="0.00"
                              className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                            />
                          </td>
                          <td className="px-4 py-4">
                            <Input
                              value={item.units}
                              onChange={(e) =>
                                handleLineItemChange(index, 'units', e.target.value)
                              }
                              placeholder="Kg, Nos..."
                              className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                            />
                          </td>
                          <td className="px-4 py-4">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) =>
                                handleLineItemChange(index, 'rate', e.target.value)
                              }
                              placeholder="0.00"
                              className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                            />
                          </td>
                          <td className="px-4 py-4">
                            <Input
                              type="number"
                              readOnly
                              value={item.amount}
                              placeholder="0.00"
                              className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600"
                            />
                          </td>
                          <td className="px-4 py-4 text-right">
                            <button
                              type="button"
                              onClick={() => handleRemoveLineItem(index)}
                              disabled={lineItems.length === 1}
                              className="inline-flex items-center gap-2 rounded-full border border-red-100 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <Trash2 className="h-4 w-4" />
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-gray-50">
                        <td
                          colSpan={6}
                          className="px-4 py-3 text-right text-sm font-semibold text-gray-700"
                        >
                          Subtotal (Taxable Amount)
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                          {formatCurrency(summary.subtotal)}
                        </td>
                        <td></td>
                      </tr>
                      <tr className="border-t bg-gray-50">
                        <td
                          colSpan={6}
                          className="px-4 py-3 text-right text-sm font-semibold text-gray-700"
                        >
                          {gstFooterLabel}
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                          {formatCurrency(lineItemsGstTotal)}
                        </td>
                        <td></td>
                      </tr>
                      <tr className="border-t bg-gray-100">
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 text-sm font-bold uppercase text-gray-900">
                          Total
                        </td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                          {formatTotalQuantity(summary.totalQuantity)}
                        </td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                          {formatCurrency(lineItemsGrandTotal)}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <div className="space-y-4 rounded-2xl border border-gray-100 bg-gray-50 p-1">
                  {lineItems.map((item, index) => (
                    <div
                      key={`line-item-card-${index}`}
                      className="rounded-2xl border border-white bg-white p-4 shadow-sm"
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-700">Item #{index + 1}</p>
                        <button
                          type="button"
                          onClick={() => handleRemoveLineItem(index)}
                          disabled={lineItems.length === 1}
                          className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 border-red-100 text-red-600 hover:border-red-300 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                          Remove
                        </button>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            Name / Description <span className="text-red-500">*</span>
                          </label>
                          <Input
                            value={item.name}
                            onChange={(e) => handleLineItemChange(index, 'name', e.target.value)}
                            placeholder="Item or service name"
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            SAC / HSN Code
                          </label>
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            value={item.hsnSacCode}
                            onChange={(e) =>
                              handleLineItemChange(index, 'hsnSacCode', e.target.value)
                            }
                            placeholder="Code"
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Quantity <span className="text-red-500">*</span>
                            </label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) =>
                                handleLineItemChange(index, 'quantity', e.target.value)
                              }
                              placeholder="0.00"
                              className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Units
                            </label>
                            <Input
                              value={item.units}
                              onChange={(e) =>
                                handleLineItemChange(index, 'units', e.target.value)
                              }
                              placeholder="Kg, Nos..."
                              className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Rate <span className="text-red-500">*</span>
                            </label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.rate}
                              onChange={(e) =>
                                handleLineItemChange(index, 'rate', e.target.value)
                              }
                              placeholder="0.00"
                              className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                            />
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Amount
                            </label>
                            <Input
                              type="number"
                              readOnly
                              value={item.amount}
                              placeholder="0.00"
                              className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="space-y-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm">
                    <div className="flex items-center justify-between font-semibold text-gray-700">
                      <span>Subtotal (Taxable Amount)</span>
                      <span className="text-gray-900">{formatCurrency(summary.subtotal)}</span>
                    </div>
                    <div className="flex items-center justify-between font-semibold text-gray-700">
                      <span>{gstFooterLabel}</span>
                      <span className="text-gray-900">{formatCurrency(lineItemsGstTotal)}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-gray-200 pt-2 font-bold text-gray-900">
                      <span className="uppercase">
                        Total
                        {summary.totalQuantity > 0 && (
                          <span className="ml-2 font-semibold normal-case text-gray-700">
                            (Qty: {formatTotalQuantity(summary.totalQuantity)})
                          </span>
                        )}
                      </span>
                      <span>{formatCurrency(lineItemsGrandTotal)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {amountInWords && (
            <p className="px-1 text-sm text-gray-800">
              Total amount(in words): {amountInWords}
            </p>
          )}

          <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div>
              <p className="text-sm font-semibold text-gray-900">HSN/SAC Tax Summary</p>
              <p className="text-sm text-gray-500">
                Tax breakdown grouped by SAC/HSN code from line items.
              </p>
            </div>

            <div className="mt-6 overflow-x-auto rounded-2xl border border-gray-100">
              <table className="min-w-full border-collapse text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold">
                      HSN/SAC
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-right font-semibold">
                      Taxable Value
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-right font-semibold min-w-[120px]">
                      Central Tax Rate
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-right font-semibold">
                      Central Tax Amount
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-right font-semibold min-w-[120px]">
                      State/UT Tax Rate
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-right font-semibold">
                      State/UT Tax Amount
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-right font-semibold min-w-[120px]">
                      Integrated Tax Rate
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-right font-semibold">
                      Integrated Tax Amount
                    </th>
                    <th className="border border-gray-200 px-4 py-3 text-right font-semibold">
                      Total Tax Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {hsnSacTaxSummary.rows.length > 0 ? (
                    hsnSacTaxSummary.rows.map((row) => (
                      <tr key={`hsn-sac-tax-${row.code}`} className="text-gray-800">
                        <td className="border border-gray-200 px-4 py-3 font-semibold">
                          {row.code}
                        </td>
                        <td className="border border-gray-200 px-4 py-3 text-right">
                          {formatTaxTableAmount(row.taxableValue)}
                        </td>
                        <td className="border border-gray-200 px-4 py-3 text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.centralRateInput}
                            onChange={(e) =>
                              handleHsnSacRateChange(row.code, 'centralRate', e.target.value)
                            }
                            placeholder="%"
                            className="h-9 w-full min-w-[72px] rounded-xl border-gray-200 bg-gray-50 px-3 text-right text-sm"
                          />
                        </td>
                        <td className="border border-gray-200 px-4 py-3 text-right">
                          {formatTaxTableAmount(row.centralAmount)}
                        </td>
                        <td className="border border-gray-200 px-4 py-3 text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.stateUtRateInput}
                            onChange={(e) =>
                              handleHsnSacRateChange(row.code, 'stateUtRate', e.target.value)
                            }
                            placeholder="%"
                            className="h-9 w-full min-w-[72px] rounded-xl border-gray-200 bg-gray-50 px-3 text-right text-sm"
                          />
                        </td>
                        <td className="border border-gray-200 px-4 py-3 text-right">
                          {formatTaxTableAmount(row.stateAmount)}
                        </td>
                        <td className="border border-gray-200 px-4 py-3 text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.igstRateInput}
                            onChange={(e) =>
                              handleHsnSacRateChange(row.code, 'igstRate', e.target.value)
                            }
                            placeholder="%"
                            className="h-9 w-full min-w-[72px] rounded-xl border-gray-200 bg-gray-50 px-3 text-right text-sm"
                          />
                        </td>
                        <td className="border border-gray-200 px-4 py-3 text-right">
                          {formatTaxTableAmount(row.igstAmount)}
                        </td>
                        <td className="border border-gray-200 px-4 py-3 text-right font-semibold">
                          {formatTaxTableAmount(row.totalTax)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={9}
                        className="border border-gray-200 px-4 py-4 text-center text-gray-500"
                      >
                        Tax breakdown will appear once line items are added.
                      </td>
                    </tr>
                  )}
                </tbody>
                {hsnSacTaxSummary.rows.length > 0 && (
                  <tfoot>
                    <tr className="bg-gray-50 font-bold text-gray-900">
                      <td className="border border-gray-200 px-4 py-3">Total</td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        {formatTaxTableAmount(hsnSacTaxSummary.totals.taxableValue)}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right"></td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        {formatTaxTableAmount(hsnSacTaxSummary.totals.centralAmount)}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right"></td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        {formatTaxTableAmount(hsnSacTaxSummary.totals.stateAmount)}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right"></td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        {formatTaxTableAmount(hsnSacTaxSummary.totals.igstAmount)}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        {formatTaxTableAmount(hsnSacTaxSummary.totals.totalTax)}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </section>

          {/* Row 5: Discount & Charges */}
          <section>
            <div className="w-full max-w-md">
              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-gray-900">Discount & Charges</p>
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500">
                      Shipping / Handling
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={charges.shippingHandling}
                      onChange={(e) =>
                        handleChargesChange('shippingHandling', e.target.value)
                      }
                      placeholder="0.00"
                      className="mt-1 h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500">Insurance</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={charges.insurance}
                      onChange={(e) => handleChargesChange('insurance', e.target.value)}
                      placeholder="0.00"
                      className="mt-1 h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Row 6: Signatures + Summary */}
          <section className="flex flex-col gap-6 lg:flex-row">
            <div className="flex-1 min-w-0 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-sm font-semibold text-gray-900">Signatures</p>
              <div className="mt-4 space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500">Vendor Sign (By clicking the checkbox you are validating the Quotation with Digital Signature)</label>
                  <button
                    type="button"
                    onClick={handleVendorSignToggle}
                    aria-pressed={isVendorSigned}
                    className={`mt-1 flex h-12 w-full items-center rounded-2xl border px-4 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 ${
                      isVendorSigned
                        ? 'border-green-600 bg-green-600 text-white'
                        : 'border-green-200 bg-green-50 text-green-700 hover:border-green-300 hover:bg-green-100'
                    }`}
                  >
                    <span
                      className={`mr-3 flex h-5 w-5 items-center justify-center rounded border ${
                        isVendorSigned ? 'border-white bg-white/20' : 'border-green-400 bg-white'
                      }`}
                    >
                      {isVendorSigned && <Check className="h-3 w-3" />}
                    </span>
                    <span
                      className="text-left"
                      style={{ color: isVendorSigned ? '#ffffff' : '#065f46' }}
                    >
                      Validate the Quotation with Digital Signature
                    </span>
                  </button>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">Client Sign</label>
                  <Input
                    readOnly
                    disabled
                    value={formData.clientSign}
                    onChange={(e) => handleFormChange('clientSign', e.target.value)}
                    placeholder="Client signature captured automatically"
                    className="mt-1 h-12 rounded-2xl border-gray-200 bg-gray-100 px-4 text-sm text-gray-500 cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            <div className="w-full min-w-[320px] flex-shrink-0 lg:w-[320px]">
              <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-gray-900">Summary</p>
                <dl className="mt-4 space-y-3 text-sm text-gray-600">
                  <div className="flex items-center justify-between">
                    <dt>Subtotal</dt>
                    <dd>₹ {summary.subtotal.toFixed(2)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt>Shipping / Handling</dt>
                    <dd>₹ {summary.shippingHandling.toFixed(2)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt>Insurance</dt>
                    <dd>₹ {summary.insurance.toFixed(2)}</dd>
                  </div>
                  <div className="flex items-center justify-between font-semibold text-gray-900">
                    <dt>Net Total (Before Tax)</dt>
                    <dd>₹ {summary.netTotalBeforeTax.toFixed(2)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt>Total Tax Amount</dt>
                    <dd>₹ {lineItemsGstTotal.toFixed(2)}</dd>
                  </div>
                  <div className="flex items-center justify-between text-base font-bold text-green-700">
                    <dt>Grand Total</dt>
                    <dd>₹ {documentGrandTotal.toFixed(2)}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </section>

          <div className="flex items-end justify-end">
            <PrimaryButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Quotation'}
            </PrimaryButton>
          </div>

          {feedback.error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {feedback.error}
            </div>
          )}
          {feedback.success && (
            <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {feedback.success}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EstimationQuotation;

